/** Live updates: one server-sent-events stream per tab. Events refresh the affected queries; when the
 * stream is down, list queries fall back to polling (see useLivePolling). */
import { fetchEventSource } from "@microsoft/fetch-event-source";
import type { QueryClient } from "@tanstack/react-query";
import { create } from "zustand";
import { freshToken, refresh } from "./auth";
import { keys } from "./queries";
import type { Reel, ReelSummary } from "./types";

type LiveState = { connected: boolean; set: (c: boolean) => void };
export const useLive = create<LiveState>((set) => ({ connected: false, set: (connected) => set({ connected }) }));

type Ev = {
  type: string;
  newspaper_id?: string;
  reel_id?: string;
  job_id?: number;
  status?: string;
  version?: number;
  progress?: number;
  step?: string;
  kind?: string;
  by?: string;
};

/** Hooks the editor registers so it can decide what a remote change means for unsaved work. */
export const reelListeners = new Map<string, (e: Ev) => void>();

function patchJob(qc: QueryClient, e: Ev) {
  const upd = <T extends { job?: Reel["job"]; id: string }>(r: T): T =>
    r.id === e.reel_id && r.job && r.job.id === e.job_id
      ? { ...r, job: { ...r.job, progress: e.progress ?? r.job.progress, step: e.step ?? r.job.step, state: "running" } }
      : r;
  qc.setQueriesData<{ items: ReelSummary[] }>({ queryKey: ["reels"] }, (d) => (d ? { ...d, items: d.items.map(upd) } : d));
  if (e.reel_id) qc.setQueryData<Reel>(keys.reel(e.reel_id), (r) => (r ? upd(r) : r));
}

function handle(qc: QueryClient, e: Ev) {
  const np = e.newspaper_id ?? "";
  switch (e.type) {
    case "job.progress":
      if (e.kind === "build_reel") patchJob(qc, e);
      qc.setQueryData(keys.job(e.job_id!), (j: object | undefined) => (j ? { ...j, progress: e.progress, step: e.step } : j));
      break;
    case "job.state":
      qc.invalidateQueries({ queryKey: keys.job(e.job_id!) });
      if (e.kind === "build_reel" || e.kind === "estimate_lengths") {
        qc.invalidateQueries({ queryKey: keys.reels(np) });
        qc.invalidateQueries({ queryKey: ["counts", np] });
      }
      break;
    case "reel.updated":
    case "reel.lengths":
    case "reel.thumb":
    case "reel.created":
    case "reel.deleted":
      qc.invalidateQueries({ queryKey: keys.reels(np) });
      if (e.type !== "reel.thumb" && e.type !== "reel.lengths") qc.invalidateQueries({ queryKey: ["counts", np] });
      qc.invalidateQueries({ queryKey: keys.trash(np) });
      break;
    case "newspaper.updated":
      qc.invalidateQueries({ queryKey: keys.newspaper(np) });
      qc.invalidateQueries({ queryKey: keys.newspapers });
      qc.invalidateQueries({ queryKey: keys.sponsors(np) });
      break;
  }
  if (e.reel_id) {
    const own = reelListeners.get(e.reel_id);
    if (own) own(e);
    else if (e.type !== "job.progress") qc.invalidateQueries({ queryKey: keys.reel(e.reel_id) });
  }
}

export function connectLive(qc: QueryClient): () => void {
  const ctrl = new AbortController();
  let stopped = false;

  async function run() {
    while (!stopped) {
      const token = await freshToken();
      if (!token) return;
      try {
        await fetchEventSource("/api/events", {
          signal: ctrl.signal,
          headers: { Authorization: `Bearer ${token}` },
          openWhenHidden: true,
          async onopen(res) {
            if (res.status === 401) {
              await refresh();
              throw new Error("retry");
            }
            if (!res.ok) throw new Error(`events ${res.status}`);
            useLive.getState().set(true);
          },
          onmessage(msg) {
            if (msg.event === "token_expired") throw new Error("token");
            if (!msg.data || msg.event === "ready") return;
            try {
              handle(qc, JSON.parse(msg.data));
            } catch {
              /* ignore malformed */
            }
          },
          onclose() {
            throw new Error("closed");
          },
          onerror(err) {
            throw err;      // handled below: reconnect with a fresh token
          },
        });
      } catch {
        /* reconnect */
      }
      useLive.getState().set(false);
      if (stopped) return;
      await new Promise((r) => setTimeout(r, 2500));
      // something may have changed while we were away
      qc.invalidateQueries({ queryKey: ["reels"] });
    }
  }
  run();
  return () => {
    stopped = true;
    ctrl.abort();
    useLive.getState().set(false);
  };
}

/** Poll interval for list queries: fast while something builds and the stream is down, slow otherwise. */
export function pollInterval(anyBuilding: boolean): number | false {
  const connected = useLive.getState().connected;
  if (anyBuilding) return connected ? 15_000 : 3_000;
  return connected ? false : 30_000;
}
